import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useUser } from '@clerk/clerk-react';
import { format, startOfWeek, endOfWeek, eachDayOfInterval, startOfMonth, endOfMonth } from 'date-fns';
import axios from 'axios';
import { getToken } from './utils';
import { getApiUrl } from '../../utils/apiUtils';
import CalendarGrid from './CalendarGrid';
import NavigationHeader from './NavigationHeader';
import RegimenDayModal from './RegimenDayModal';
import useSwapManager from './useSwapManager';
import { debugLog, debugError, debugGroupCollapsed } from '../../utils/debug';
import { getEventCategoryDisplayName } from './trainingPlanHelpers';

// Helper to format phase names for display
const formatPhaseName = (phaseName) => {
  const phaseMap = {
    'BaseBuilding': 'Base Building',
    'Build': 'Build',
    'Peak': 'Peak',
    'Taper': 'Taper',
    'Race': 'Race Week'
  };
  return phaseMap[phaseName] || phaseName;
};

// Training Plan Overview component
const TrainingPlanOverview = ({ meta, viewedDate, viewedRegimen, isCollapsed, onToggleCollapse }) => {
  if (!meta) return null;
  
  const {
    phase_name,
    phase_goal,
    target_volume_km,
    min_volume_km,
    max_volume_km,
    baseline_volume_km,
    race_date,
    training_start_date,
    has_race,  // Bug #12 B2: explicit flag for whether user has a race goal
  } = meta;
  
  // Bug #12 B2: Check if this is a no-race user
  const isNoRaceUser = has_race === false || (!race_date && !meta.weeks_until_race);
  const isBeginnerVolume = baseline_volume_km && baseline_volume_km <= 15;
  
  // Bug #14C: Check if viewing a chat-created plan with special event category
  // Defensive null check: event_category may be missing for legacy regimens
  const viewedEventCategory = viewedRegimen?.event_category || null;
  const customPlanTitle = getEventCategoryDisplayName(viewedEventCategory);
  const isViewingChatCreatedPlan = customPlanTitle && isNoRaceUser && viewedRegimen;
  
  // Compute total_weeks from training_start_date to race_date
  const computeTotalWeeks = () => {
    if (isNoRaceUser) return null; // No total weeks for no-race users
    if (!training_start_date || !race_date) return meta.total_weeks;
    const startDate = new Date(training_start_date);
    const raceD = new Date(race_date);
    const totalDays = Math.floor((raceD - startDate) / (1000 * 60 * 60 * 24));
    return Math.max(1, Math.ceil(totalDays / 7));
  };
  
  // Compute dynamic week_index based on viewedDate (not just "today")
  const computeViewedWeekIndex = (totalWeeks) => {
    if (!training_start_date) return meta.week_index;
    const startDate = new Date(training_start_date);
    const viewed = viewedDate || new Date();
    const daysSinceStart = Math.floor((viewed - startDate) / (1000 * 60 * 60 * 24));
    const weekIdx = Math.floor(daysSinceStart / 7) + 1;
    if (isNoRaceUser) return weekIdx; // No upper bound for no-race users
    return Math.max(1, Math.min(totalWeeks, weekIdx));
  };
  
  // Compute dynamic weeks_until_race based on viewedDate
  const computeWeeksUntilRace = () => {
    if (isNoRaceUser) return null; // No weeks until race for no-race users
    if (!race_date) return meta.weeks_until_race;
    // Parse date strings properly (YYYY-MM-DD format from API)
    const raceD = new Date(race_date + 'T00:00:00');
    const viewed = viewedDate || new Date();
    const daysUntil = Math.floor((raceD - viewed) / (1000 * 60 * 60 * 24));
    const weeksUntil = Math.max(0, Math.ceil(daysUntil / 7));
    return weeksUntil;
  };
  
  // Phase definitions with thresholds (based on weeks_until_race)
  const phaseDefinitions = [
    { name: 'Race', displayName: 'Race Week', maxWeeksUntil: 0, color: 'bg-red-500', goal: 'Race day - execute the training plan' },
    { name: 'Taper', displayName: 'Taper', maxWeeksUntil: 2, color: 'bg-amber-500', goal: 'Reduce volume while maintaining intensity to peak for race day' },
    { name: 'Peak', displayName: 'Peak', maxWeeksUntil: 6, color: 'bg-orange-500', goal: 'Maximize fitness with high-intensity workouts and race-specific training' },
    { name: 'Build', displayName: 'Build', maxWeeksUntil: 12, color: 'bg-blue-500', goal: 'Increase training intensity and volume while maintaining aerobic base' },
    { name: 'Base', displayName: 'Base Building', maxWeeksUntil: Infinity, color: 'bg-green-500', goal: 'Build aerobic endurance and establish consistent training volume' },
  ];
  
  // Get phase for a given weeks_until_race value
  const getPhaseForWeeksUntil = (weeksUntil) => {
    for (const phase of phaseDefinitions) {
      if (weeksUntil <= phase.maxWeeksUntil) return phase;
    }
    return phaseDefinitions[phaseDefinitions.length - 1]; // Base
  };
  
  const total_weeks = computeTotalWeeks();
  const week_index = computeViewedWeekIndex(total_weeks);
  const weeks_until_race = computeWeeksUntilRace();
  
  // For no-race users, use a simplified phase display
  const currentPhase = isNoRaceUser 
    ? { name: 'Base', displayName: phase_name || 'Base / Maintenance', color: 'bg-green-500', goal: phase_goal }
    : getPhaseForWeeksUntil(weeks_until_race);
  
  // Calculate phase segments for the arc bar
  // Work backwards from race, assigning weeks to phases
  const getPhaseSegments = () => {
    if (isNoRaceUser || total_weeks <= 0) return []; // No arc bar for no-race users
    
    const segments = [];
    let remaining = total_weeks;
    
    // Taper: last 2 weeks (weeks where weeks_until_race <= 2)
    const taperWeeks = Math.min(2, remaining);
    if (taperWeeks > 0) {
      segments.unshift({ name: 'Taper', weeks: taperWeeks, color: 'bg-amber-500' });
      remaining -= taperWeeks;
    }
    
    // Peak: weeks 3-6 before race (weeks where weeks_until_race 3-6)
    const peakWeeks = Math.min(4, remaining);
    if (peakWeeks > 0) {
      segments.unshift({ name: 'Peak', weeks: peakWeeks, color: 'bg-orange-500' });
      remaining -= peakWeeks;
    }
    
    // Build: weeks 7-12 before race
    const buildWeeks = Math.min(6, remaining);
    if (buildWeeks > 0) {
      segments.unshift({ name: 'Build', weeks: buildWeeks, color: 'bg-blue-500' });
      remaining -= buildWeeks;
    }
    
    // Base: everything else (weeks where weeks_until_race > 12)
    if (remaining > 0) {
      segments.unshift({ name: 'Base', weeks: remaining, color: 'bg-green-500' });
    }
    
    return segments;
  };
  
  const segments = getPhaseSegments();
  
  // Find which segment the current phase falls into (by matching phase name)
  const getCurrentSegmentIndex = () => {
    // Map current phase name to segment name
    const phaseToSegment = {
      'Race': 'Taper', // Race week is part of taper segment visually
      'Taper': 'Taper',
      'Peak': 'Peak',
      'Build': 'Build',
      'Base': 'Base',
    };
    const targetSegment = phaseToSegment[currentPhase.name] || currentPhase.name;
    return segments.findIndex(s => s.name === targetSegment);
  };
  
  const currentSegmentIndex = getCurrentSegmentIndex();
  
  // Use dynamic phase goal or fallback to API-provided one
  const displayPhaseGoal = currentPhase.goal || phase_goal;
  
  // Bug #12 B2: Get display text for header based on race status
  const getHeaderSubtext = () => {
    if (isNoRaceUser) {
      return 'No race selected';
    }
    if (total_weeks) {
      return `Week ${week_index} of ${total_weeks}`;
    }
    return `Week ${week_index}`;
  };
  
  const getWeeksToRaceText = () => {
    if (isViewingChatCreatedPlan) {
      return 'Chat-created';
    }
    if (isNoRaceUser) {
      return isBeginnerVolume ? 'Getting started' : 'Base building';
    }
    if (weeks_until_race > 0) {
      return `${weeks_until_race} week${weeks_until_race !== 1 ? 's' : ''} to race`;
    }
    return 'Race week!';
  };
  
  // Format the viewed week start date
  const formatViewedWeekDate = () => {
    if (!viewedRegimen?.start_of_week_date) return null;
    const date = new Date(viewedRegimen.start_of_week_date + 'T00:00:00');
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };
  
  // Bug #14C: Simplified card for chat-created plans
  if (isViewingChatCreatedPlan) {
    return (
      <div className="bg-gray-800/60 rounded-xl border border-gray-700/50 overflow-hidden">
        <button
          onClick={onToggleCollapse}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-700/30 transition-colors text-left"
        >
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium text-gray-300">{customPlanTitle}</h3>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Chat-created</span>
            <svg
              className={`w-4 h-4 text-gray-500 transition-transform ${isCollapsed ? '' : 'rotate-180'}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </button>
        
        {!isCollapsed && (
          <div className="px-4 pb-4 pt-0">
            <p className="text-xs text-gray-400 mb-2">
              This plan was created via chat and isn't linked to a specific race date, so phase and race countdown are not shown.
            </p>
            {formatViewedWeekDate() && (
              <p className="text-xs text-gray-500">
                Viewing week of {formatViewedWeekDate()}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }
  
  return (
    <div className="bg-gray-800/60 rounded-xl border border-gray-700/50 overflow-hidden">
      {/* Header row - always visible, clickable to toggle */}
      <button
        onClick={onToggleCollapse}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-700/30 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-medium text-gray-300">Training Plan</h3>
          <span className="text-xs text-gray-500">
            · {getHeaderSubtext()}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">
            {getWeeksToRaceText()}
          </span>
          <svg
            className={`w-4 h-4 text-gray-500 transition-transform ${isCollapsed ? '' : 'rotate-180'}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>
      
      {/* Collapsible content */}
      {!isCollapsed && (
        <div className="px-4 pb-4 pt-0">
          {/* Main info */}
          <div className="mb-3">
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-lg font-semibold text-white">
                {currentPhase.displayName}
              </span>
            </div>
            {displayPhaseGoal && (
              <p className="text-xs text-gray-400 line-clamp-2">{displayPhaseGoal}</p>
            )}
            {/* Special message for beginner no-race users */}
            {isNoRaceUser && isBeginnerVolume && (
              <p className="text-xs text-green-400/80 mt-1">
                Focus on short, easy runs to build consistency. 🌱
              </p>
            )}
          </div>
          
          {/* Volume target */}
          <div className="text-sm text-gray-300 mb-4">
            <span className="text-gray-500">Target: </span>
            <span className="font-medium text-white">{target_volume_km.toFixed(1)} km</span>
            <span className="text-gray-500 text-xs ml-1">
              ({min_volume_km.toFixed(1)}–{max_volume_km.toFixed(1)} km)
            </span>
          </div>
          
          {/* Training arc bar - only show for users with a race */}
          {!isNoRaceUser && segments.length > 0 && (
            <div className="space-y-2">
              <div className="flex h-2 rounded-full overflow-hidden bg-gray-700/50">
                {segments.map((segment, idx) => {
                  const widthPercent = (segment.weeks / total_weeks) * 100;
                  const isCurrentPhase = idx === currentSegmentIndex;
                  return (
                    <div
                      key={segment.name}
                      className={`${segment.color} ${isCurrentPhase ? 'ring-2 ring-white ring-inset' : 'opacity-60'} transition-all`}
                      style={{ width: `${widthPercent}%` }}
                      title={`${segment.name}: ${segment.weeks} week${segment.weeks !== 1 ? 's' : ''}`}
                    />
                  );
                })}
              </div>
              
              {/* Phase labels */}
              <div className="flex text-[10px] text-gray-500">
                {segments.map((segment, idx) => {
                  const widthPercent = (segment.weeks / total_weeks) * 100;
                  const isCurrentPhase = idx === currentSegmentIndex;
                  return (
                    <div
                      key={segment.name}
                      className={`truncate ${isCurrentPhase ? 'text-gray-300 font-medium' : ''}`}
                      style={{ width: `${widthPercent}%` }}
                    >
                      {segment.name}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const RunSchedule = () => {
  const { user } = useUser();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [regimens, setRegimens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [onboardingData, setOnboardingData] = useState(null);
  const [trainingPlanMeta, setTrainingPlanMeta] = useState(null);
  const [trainingPlanCollapsed, setTrainingPlanCollapsed] = useState(() => {
    // Persist collapse state in localStorage
    const saved = localStorage.getItem('trainingPlanCollapsed');
    return saved === 'true';
  });
  
  // Track component mount state and active requests
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef(null);
  const retryTimeoutRef = useRef(null);
  const fetchInProgressRef = useRef(false);
  const fetchStartTimeRef = useRef(null); // Track when fetch starts to detect long-running fetches

  const [viewMode] = useState('week'); // For month view toggle
  const [showRunModal, setShowRunModal] = useState(false);
  const [selectedRun, setSelectedRun] = useState(null);
  const [selectedRunType, setSelectedRunType] = useState(null);

  const isGuiOpsEnabled = (typeof process !== 'undefined' ? process.env.REACT_APP_GUI_WORKOUTS_V1 : undefined) !== 'false';

  // Get the days for the current view
  const getViewDays = () => {
    if (viewMode === 'week') {
      // Always use Monday-based weeks to match our regimens structure
      const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 }); // Monday
      const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
      return eachDayOfInterval({ start: weekStart, end: weekEnd });
    } else if (viewMode === 'month') {
      // For month view, include leading and trailing days to fill the calendar grid
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      
      // Get the start of the week containing the first day of the month (Monday-based)
      const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 }); // Monday
      
      // Get the end of the week containing the last day of the month
      const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 }); // Sunday
      
      return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
    }
    return [currentDate]; // Default to single day
  };

  const viewDays = getViewDays();

  const fetchWithRetry = async (url, options = {}, maxRetries = 3) => {
    let lastError;
    
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const response = await fetch(url, options);
        return response; // Return on first success
      } catch (error) {
        lastError = error;
        
        // Don't retry on the last attempt
        if (attempt < maxRetries - 1) {
          // Calculate exponential backoff delay: 500ms, 1s, 2s
          const delay = 500 * Math.pow(2, attempt);
          debugLog(`🔄 [FETCH RETRY] Attempt ${attempt + 1}/${maxRetries} failed for ${url}, retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    // All retries failed, throw the last error
    throw lastError;
  };

  const fetchOnboardingData = useCallback(async () => {
    try {
      const token = await getToken(user);
      const API_URL = getApiUrl(); // Get at runtime
      const response = await fetchWithRetry(`${API_URL}/user/onboarding-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const result = await response.json();
        setOnboardingData(result.onboarding_data);
        // Removed verbose onboarding data dumps - only log if needed for debugging
      }
    } catch (error) {
      debugError('RunSchedule: Error fetching onboarding data:', error);
    }
  }, [user]);

  // Fetch training plan metadata (head coach framework)
  const fetchTrainingPlanMeta = useCallback(async () => {
    try {
      const token = await getToken(user);
      const API_URL = getApiUrl();
      const response = await fetchWithRetry(`${API_URL}/api/regimens/weekly/meta`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const result = await response.json();
        setTrainingPlanMeta(result);
        debugLog('📊 [PLAN META] Fetched training plan metadata:', result);
      }
    } catch (error) {
      // Non-critical - don't block UI if meta fetch fails
      debugError('RunSchedule: Error fetching training plan meta:', error);
    }
  }, [user]);

  const fetchRegimensData = useCallback(async (isRefresh = false, retryCount = 0) => {
    // 🎯 FIX: Cancel long-running fetches when event-based refresh is triggered
    // This prevents event-based refreshes from being blocked by slow initial fetches
    if (fetchInProgressRef.current && isRefresh) {
      const fetchDuration = fetchStartTimeRef.current ? Date.now() - fetchStartTimeRef.current : 0;
      if (fetchDuration > 10000) {
        debugLog(`🔄 [REGIMENS] Canceling long-running fetch (${fetchDuration}ms) to allow event-based refresh`);
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        fetchInProgressRef.current = false;
        if (isMountedRef.current) {
          setLoading(false);
          setRefreshing(false);
        }
      } else {
        debugLog('🔄 [REGIMENS] Refresh requested but fetch in progress (recent), skipping to avoid cancellation');
        return;
      }
    }
    
    // 🎯 FIX: Prevent duplicate simultaneous requests
    if (fetchInProgressRef.current && !isRefresh) {
      debugLog('🔄 [REGIMENS] Fetch already in progress, skipping duplicate request');
      return;
    }
    
    const maxRetries = 3;
    let shouldRetry = false;
    
    // 🎯 FIX: Cancel any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;
    
    // Clear any pending retry timeout
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    
    fetchInProgressRef.current = true;
    fetchStartTimeRef.current = Date.now(); // Track when fetch starts
    
    try {
      if (!isMountedRef.current) return; // Component unmounted
      
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      const API_URL = getApiUrl(); // Get at runtime
      const token = await getToken(user);
      
      // 🎯 FIX: Add timeout and signal to prevent hanging and allow cancellation
      // Increased timeout to 15s to handle network latency (was 5s, causing frequent timeouts)
      const response = await axios.get(`${API_URL}/api/regimens/weekly?limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000, // 15 second timeout (allows for network latency while preventing indefinite hanging)
        signal // Allow cancellation
      });
      
      // Check if component is still mounted before setting state
      if (!isMountedRef.current) return;
      
      // Regimens data fetched successfully
      const fetchedRegimens = response.data || [];
      // Deduplicate regimens by ID to prevent React key warnings
      const uniqueRegimens = fetchedRegimens.filter((regimen, index, self) =>
        index === self.findIndex(r => r.id === regimen.id)
      );
      setRegimens(uniqueRegimens);
      
      // 🎯 FIX: Clear loading state immediately on success
      if (isRefresh) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
      
      fetchInProgressRef.current = false;
    } catch (error) {
      fetchInProgressRef.current = false;
      
      // 🎯 FIX: Don't log or handle errors if request was aborted or component unmounted
      if (axios.isCancel(error) || !isMountedRef.current) {
        debugLog('🔄 [REGIMENS] Request canceled or component unmounted, ignoring error');
        return;
      }
      
      // Suppress timeout errors in expected scenarios
      const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
      const scheduleAlreadyDisplayed = regimens.length > 0;
      
      // Check if user is in onboarding (no schedule exists yet)
      const isInOnboarding = localStorage.getItem(`onboarding_completed_${user?.id}`) !== 'true';
      
      if (isTimeout && (scheduleAlreadyDisplayed || isInOnboarding) && !isRefresh) {
        // Expected timeout - schedule doesn't exist yet or already loaded
        return;
      }
      
      // Only log unexpected errors
      debugGroupCollapsed(`❌ [SCHEDULE] Error fetching regimens`, () => {
        debugError('Error:', error.message);
        if (error.response) {
          debugError(`Status: ${error.response.status} ${error.response.statusText}`);
        }
        if (isTimeout) {
          debugError('Timeout: 15s exceeded');
        }
      });
      
      // Check if component is still mounted before setting state
      if (!isMountedRef.current) return;
      
      // 🎯 FIX: Set empty regimens on error so calendar shows empty state instead of skeleton
      setRegimens([]);
      
      // Retry logic with exponential backoff
      if (retryCount < maxRetries && (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED')) {
        shouldRetry = true;
        const delay = 500 * Math.pow(2, retryCount);
        debugLog(`🔄 [REGIMENS RETRY] Attempt ${retryCount + 1}/${maxRetries} failed, retrying in ${delay}ms...`);
        
        // 🎯 FIX: Track retry timeout so it can be cleaned up
        retryTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            fetchRegimensData(isRefresh, retryCount + 1);
          }
        }, delay);
        
        return; // Don't clear loading state yet - will be cleared after retry succeeds or fails
      }
      
      // 🎯 FIX: Always clear loading state on final failure (no more retries)
      // This ensures calendar shows empty state instead of infinite skeleton
      if (!shouldRetry && isMountedRef.current) {
        if (isRefresh) {
          setRefreshing(false);
        } else {
          setLoading(false);
        }
      }
    }
  }, [user, regimens.length]);

  // Fetch regimens data
  useEffect(() => {
    if (!user) return;
    
    let timeoutId = null;
    
    // Check if user is currently in onboarding - skip fetch if so
    const checkOnboardingStatus = async () => {
      try {
        const token = await getToken(user);
        const API_URL = getApiUrl();
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000);
        
        const response = await fetch(`${API_URL}/user/onboarding-status`, {
          headers: { 'Authorization': `Bearer ${token}` },
          signal: controller.signal
        });
        clearTimeout(timeoutId);
        
        if (response.ok) {
          const data = await response.json();
          if (!data.onboard_complete) {
            // User is in onboarding - skip fetching regimens
            setLoading(false);
            return;
          }
        }
      } catch (e) {
        // If check fails, proceed with fetch (better to try than block)
      }
      
      // User completed onboarding - proceed with fetch
      const onboardingCompleted = localStorage.getItem(`onboarding_completed_${user.id}`);
      const paywallSeen = localStorage.getItem(`paywall_seen_${user.id}`);
      
      if (onboardingCompleted && !paywallSeen) {
        // Fresh onboarding user - delay to let schedule generation complete
        const delay = 5000;
        timeoutId = setTimeout(async () => {
          if (isMountedRef.current) {
            await fetchRegimensData();
            if (isMountedRef.current) {
              setTimeout(() => {
                if (isMountedRef.current) {
                  fetchOnboardingData();
                  fetchTrainingPlanMeta();
                }
              }, 500);
            }
          }
        }, delay);
      } else {
        // Existing user - normal delay
        const delay = 1500;
        timeoutId = setTimeout(async () => {
          if (isMountedRef.current) {
            await fetchRegimensData();
            if (isMountedRef.current) {
              setTimeout(() => {
                if (isMountedRef.current) {
                  fetchOnboardingData();
                  fetchTrainingPlanMeta();
                }
              }, 500);
            }
          }
        }, delay);
      }
    };
    
    checkOnboardingStatus();
    
    // Cleanup function to cancel requests and clear timeouts
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, [user, fetchOnboardingData, fetchRegimensData, fetchTrainingPlanMeta]);
  
  // 🎯 FIX: Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
      // Cancel any pending requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      // Clear any retry timeouts
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      fetchInProgressRef.current = false;
    };
  }, []);

  // Listen for refresh events from onboarding completion
  useEffect(() => {
    const handleRefresh = () => {
      fetchRegimensData(true);
    };

    window.addEventListener('runium:refresh-schedule', handleRefresh);
    return () => window.removeEventListener('runium:refresh-schedule', handleRefresh);
  }, [fetchRegimensData]);

  // Enhancement polling removed - chat-created workouts are now enhanced immediately

  const {
    pendingSwap,
    swapStatus,
    swapFeedback,
    handleSwapRequest,
    handleSwapCancel,
    performSwap
  } = useSwapManager({
    user,
    isSwapEnabled: isGuiOpsEnabled,
    fetchRegimensData
  });

  // Helper function to get Monday-based day index (0 = Monday, 6 = Sunday)
  const getMondayBasedDayIndex = (date) => {
    const day = date.getDay();
    return day === 0 ? 6 : day - 1; // Convert Sunday (0) to 6, others subtract 1
  };

  // Helper function to get week start (Monday) for a given date
  const getWeekStartMonday = (date) => {
    return startOfWeek(date, { weekStartsOn: 1 });
  };
  
  // Helper function to format date as YYYY-MM-DD in local timezone
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Get regimen data for a specific day
  const getRegimenForDay = (date) => {
    const weekStart = getWeekStartMonday(date);
    const dayIndex = getMondayBasedDayIndex(date);
    
    // Use local date formatting to avoid timezone issues
    const weekStartDateString = formatDateLocal(weekStart);
    
    // Process regimen for the given day
    
    // If no regimens loaded yet, return null silently
    if (regimens.length === 0) {
      return null;
    }
    
    // Find regimen for this week
    const regimen = regimens.find(r => {
      // Simple string comparison of date parts to avoid timezone issues
      const regimenDateString = r.start_of_week_date; // "2025-06-16"
      
      // Check if this regimen matches the requested week
      return regimenDateString === weekStartDateString;
    });
    
    if (!regimen) {
      // No regimen found for this week
      return null;
    }

    // Extract day data from the regimen
    const title = regimen.regimen_titles[dayIndex];
    const subtitle = regimen.regimen_subtitles[dayIndex];
    const details = regimen.regimen_details[dayIndex];
    const status = regimen.completion_status[dayIndex];
    const completionDetails = regimen.completion_details[dayIndex];
    const enhancementStatus = regimen.enhancement_status ? regimen.enhancement_status[dayIndex] : null;

    // Day data extracted from regimen

    if (!title) {
      // No workout scheduled for this day
      return null;
    }

    const workout = {
      id: `${regimen.id}-${dayIndex}`,
      regimen_id: regimen.id,
      day_index: dayIndex,
      title,
      subtitle,
      details,
      status: status || 'planned',
      completion_details: completionDetails,
      enhancement_status: enhancementStatus,
      date: date,
      distance_km: regimen.day_distances_km?.[dayIndex] ?? null,
      week_planned_volume_km: regimen.planned_volume_km ?? null
    };

    return workout;
  };

  // Handle completion status updates
  const handleQuickComplete = async (dayRegimen) => {
    try {
      const token = await getToken(user);
      
      const updateData = {
        completion_status: {
          [['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'][dayRegimen.day_index]]: 'completed'
        }
      };
      
      const API_URL = getApiUrl(); // Get at runtime
      await axios.put(`${API_URL}/api/regimens/weekly/${dayRegimen.regimen_id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Refresh the data to show the changes
      await fetchRegimensData(true);
      
    } catch (error) {
      debugError('Error quick completing regimen:', error);
    }
  };

  const handleQuickSkip = async (dayRegimen) => {
    try {
      const token = await getToken(user);
      
      const updateData = {
        completion_status: {
          [['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'][dayRegimen.day_index]]: 'skipped'
        }
      };
      
      const API_URL = getApiUrl(); // Get at runtime
      await axios.put(`${API_URL}/api/regimens/weekly/${dayRegimen.regimen_id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Refresh the data to show the changes
      await fetchRegimensData(true);
      
    } catch (error) {
      debugError('Error quick skipping regimen:', error);
    }
  };

  // Get runs for a specific day (adapted for regimens)
  const getRunsForDay = (date) => {
    const dayRegimen = getRegimenForDay(date);
    
    if (!dayRegimen) {
      return { completed: [], planned: [] };
    }

    // Convert regimen to run-like format for calendar compatibility
    const runData = {
      ...dayRegimen,
      // Add timestamp for compatibility with existing calendar logic
      timestamp: date.toISOString(),
      // Map regimen fields to run-like fields for display
      duration: null,
      notes: dayRegimen.details
      // distance_km and week_planned_volume_km are now included from dayRegimen
    };

    if (dayRegimen.status === 'completed') {
      return { completed: [runData], planned: [] };
    } else {
      return { completed: [], planned: [runData] };
    }
  };

  // Week navigation handlers
  const handlePrevWeek = useCallback(() => {
    handleSwapCancel();
    setCurrentDate((prevDate) => {
      const newDate = new Date(prevDate);
      newDate.setDate(newDate.getDate() - 7);
      return newDate;
    });
  }, [handleSwapCancel]);

  const handleNextWeek = useCallback(() => {
    handleSwapCancel();
    setCurrentDate((prevDate) => {
      const newDate = new Date(prevDate);
      newDate.setDate(newDate.getDate() + 7);
      return newDate;
    });
  }, [handleSwapCancel]);

  const handleNavigateToToday = useCallback(() => {
    handleSwapCancel();
    setCurrentDate(new Date());
  }, [handleSwapCancel]);

  // Arrow key navigation for weeks (Feature Request)
  // Mac compatibility: Uses both key name and keyCode, capture phase for better Safari support
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't interfere if user is typing in an input field
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || 
          e.target.isContentEditable) {
        return;
      }

      // Don't handle if modal is open (modal has its own arrow key navigation)
      if (showRunModal) {
        return;
      }

      // Left arrow: Previous week (Mac Safari/Chrome compatible)
      if (e.key === 'ArrowLeft' || e.keyCode === 37) {
        e.preventDefault();
        e.stopPropagation();
        handlePrevWeek();
      }
      // Right arrow: Next week (Mac Safari/Chrome compatible)
      else if (e.key === 'ArrowRight' || e.keyCode === 39) {
        e.preventDefault();
        e.stopPropagation();
        handleNextWeek();
      }
    };

    // Add event listener with capture phase for better Mac browser compatibility
    // Capture phase ensures event is caught before it bubbles, works better in Safari
    window.addEventListener('keydown', handleKeyDown, true);
    
    // Cleanup
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [handlePrevWeek, handleNextWeek, showRunModal]); // Re-attach when handlers or modal state changes

  // Modal functions

  const openRunModal = (run, type) => {
    debugLog('[RunSchedule] openRunModal called:', {
      run: run ? {
        id: run.id,
        title: run.title,
        date: run.date,
        regimen_id: run.regimen_id,
        day_index: run.day_index
      } : null,
      type,
      regimensCount: regimens.length,
      regimensStartDates: regimens.map(r => r.start_of_week_date)
    });
    setSelectedRun(run);
    setSelectedRunType(type);
    setShowRunModal(true);
  };

  const closeRunModal = () => {
    setShowRunModal(false);
    setSelectedRun(null);
    setSelectedRunType(null);
  };

  // Get current week period display text
  const getCurrentPeriodText = () => {
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
    return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`;
  };

  // Debug function to get current week's regimen data for clipboard
  const getCurrentWeekRegimenData = () => {
    const weekStart = getWeekStartMonday(currentDate);
    const weekStartDateString = weekStart.toISOString().split('T')[0];
    
    // Find regimen for current week
    const currentWeekRegimen = regimens.find(r => 
      r.start_of_week_date === weekStartDateString
    );
    
    if (!currentWeekRegimen) {
      return {
        success: false,
        message: 'No regimen found for current week',
        data: null
      };
    }

    const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const weekData = {
      week_start: weekStartDateString,
      week_display: getCurrentPeriodText(),
      regimen_id: currentWeekRegimen.id,
      created_at: currentWeekRegimen.created_at,
      updated_at: currentWeekRegimen.updated_at,
      days: []
    };

    // Build detailed day data
    for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
      const dayDate = new Date(weekStart);
      dayDate.setDate(weekStart.getDate() + dayIndex);
      
      const title = currentWeekRegimen.regimen_titles[dayIndex];
      const subtitle = currentWeekRegimen.regimen_subtitles[dayIndex];
      const details = currentWeekRegimen.regimen_details[dayIndex];
      const status = currentWeekRegimen.completion_status[dayIndex];
      const completionDetails = currentWeekRegimen.completion_details[dayIndex];

      weekData.days.push({
        day_name: dayNames[dayIndex],
        date: dayDate.toISOString().split('T')[0],
        day_index: dayIndex,
        title: title || null,
        subtitle: subtitle || null,
        details: details || null,
        status: status || 'planned',
        completion_details: completionDetails || null,
        has_regimen: !!title
      });
    }

    return {
      success: true,
      message: `Current week regimen data (${getCurrentPeriodText()})`,
      data: weekData
    };
  };

  if (loading) {
    return (
      <div className="h-full flex flex-col overflow-hidden">
        {/* Header - Simplified */}
        <div className="flex-shrink-0 px-4 pt-2 pb-0">
        <NavigationHeader
          currentDate={currentDate}
          onPrevWeek={handlePrevWeek}
          onNextWeek={handleNextWeek}
          onNavigateToToday={handleNavigateToToday}
        />
        </div>

        {/* Scrollable Content with Skeleton Loading */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6" style={{ touchAction: 'pan-y' }}>
          <CalendarGrid
            viewMode={viewMode}
            viewDays={viewDays}
            currentDate={currentDate}
            getRunsForDay={() => ({ completed: [], planned: [] })}
            handleDayClick={() => {}}
            openRunModal={() => {}}
            loading={true}
            onQuickComplete={handleQuickComplete}
            onQuickSkip={handleQuickSkip}
            getCurrentWeekRegimenData={getCurrentWeekRegimenData}
            regimens={regimens}
            onboardingData={onboardingData}
            isSwapEnabled={isGuiOpsEnabled}
            onSwapRequest={handleSwapRequest}
            pendingSwap={pendingSwap}
            swapStatus={swapStatus}
            swapFeedback={swapFeedback}
            onConfirmSwap={performSwap}
            onCancelSwap={handleSwapCancel}
          />


        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden relative run-schedule-container" tabIndex={0}>
      {/* Header - Simplified */}
      <div className="flex-shrink-0 px-4 pt-2 pb-0">
        <NavigationHeader
          currentDate={currentDate}
          onPrevWeek={handlePrevWeek}
          onNextWeek={handleNextWeek}
          onNavigateToToday={handleNavigateToToday}
        />
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6" style={{ touchAction: 'pan-y' }}>
        <div className={`transition-opacity duration-300 ${refreshing ? 'opacity-75' : 'opacity-100'}`}>
          <CalendarGrid
            viewMode={viewMode}
            viewDays={viewDays}
            currentDate={currentDate}
            getRunsForDay={getRunsForDay}
            handleDayClick={() => {}}
            openRunModal={openRunModal}
            loading={false}
            onQuickComplete={handleQuickComplete}
            onQuickSkip={handleQuickSkip}
            getCurrentWeekRegimenData={getCurrentWeekRegimenData}
            regimens={regimens}
            onboardingData={onboardingData}
            isSwapEnabled={isGuiOpsEnabled}
            onSwapRequest={handleSwapRequest}
            pendingSwap={pendingSwap}
            swapStatus={swapStatus}
            swapFeedback={swapFeedback}
            onConfirmSwap={performSwap}
            onCancelSwap={handleSwapCancel}
          />
        </div>

        {/* Training Plan Overview Card - below schedule, above share button */}
        {/* TEMPORARILY DISABLED: Feature is half-baked, removing before merge to master */}
        {/* {trainingPlanMeta && (() => {
          // Bug #14C: Find the regimen for the currently viewed week
          const viewedWeekStart = getWeekStartMonday(currentDate);
          const viewedWeekStartStr = formatDateLocal(viewedWeekStart);
          const viewedRegimen = regimens.find(r => r.start_of_week_date === viewedWeekStartStr);
          
          return (
            <TrainingPlanOverview 
              key={currentDate.toISOString()}
              meta={trainingPlanMeta} 
              viewedDate={currentDate}
              viewedRegimen={viewedRegimen}
              isCollapsed={trainingPlanCollapsed}
              onToggleCollapse={() => {
                const newValue = !trainingPlanCollapsed;
                setTrainingPlanCollapsed(newValue);
                localStorage.setItem('trainingPlanCollapsed', String(newValue));
              }}
            />
          );
        })()} */}

      </div>

      {/* Regimen Day Modal */}
      {showRunModal && selectedRun && (
        <RegimenDayModal
          isOpen={showRunModal}
          onClose={closeRunModal}
          run={selectedRun}
          type={selectedRunType}
          onSuccess={() => fetchRegimensData(true)}
          regimens={regimens}
          onWorkoutChange={(newWorkout) => {
            setSelectedRun(newWorkout);
          }}
        />
      )}
    </div>
  );
};

export default RunSchedule; 